<script lang="ts">
  import { onMount } from 'svelte'
  import { tStore } from '../../i18n'
  import { sourcesApi, type Source } from '../../api/sources'
  import { ApiError, isApiError } from '../../api/errors'
  import PageHeader from '../../components/PageHeader.svelte'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'
  import { formatDateTime } from '../../composables/format'
  import { retrievalTriggerErrorMessage } from '../../composables/retrievalTriggerError'

  const toast = useToast()
  const { start } = useAbortable()

  let sources = $state<Source[]>([])
  let loading = $state(true)
  let loadError = $state<ApiError | null>(null)

  // Per-source in-flight guard so one running trigger doesn't disable the rest.
  let pending = $state<Set<number>>(new Set())

  const enabledSources = $derived(sources.filter((s) => s.enabled))

  onMount(() => {
    void loadSources()
  })

  async function loadSources() {
    const signal = start()
    loading = true
    loadError = null
    try {
      sources = await sourcesApi.list({ signal })
    } catch (err) {
      if (signal.aborted) return // navigated away mid-request
      loadError = toApiError(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  async function trigger(source: Source) {
    pending = new Set(pending).add(source.id)
    try {
      await sourcesApi.triggerRetrieval(source.id)
      toast.show($tStore('common.collectionStarted', { name: source.displayName }), 'success')
    } catch (err) {
      toast.show(retrievalTriggerErrorMessage(err), 'error')
    } finally {
      const next = new Set(pending)
      next.delete(source.id)
      pending = next
    }
  }

  function toApiError(err: unknown): ApiError {
    if (isApiError(err)) return err
    return new ApiError({
      status: 0,
      code: 'UNKNOWN',
      message: err instanceof Error ? err.message : String(err),
    })
  }
</script>

<section class="collection">
  <PageHeader
    title={$tStore('views.collection.title')}
    subtitle={$tStore('views.collection.subtitle')}
  >
    {#snippet actions()}
      <button class="btn btn--secondary" onclick={loadSources} disabled={loading}>
        {$tStore('views.collection.refresh')}
      </button>
    {/snippet}
  </PageHeader>

  {#if loading}
    <p class="muted">{$tStore('views.collection.loading')}</p>
  {:else if loadError}
    <p class="error">{loadError.message}</p>
  {:else if enabledSources.length === 0}
    <p class="muted">{$tStore('views.collection.empty')}</p>
  {:else}
    <ul class="collection__list">
      {#each enabledSources as source (source.id)}
        <li class="card collection__item">
          <div class="collection__meta">
            <span class="collection__name">{source.displayName}</span>
            <span class="muted collection__last">
              {$tStore('views.collection.lastRun')}: {formatDateTime(source.lastRunAt)}
            </span>
          </div>
          <button
            class="btn btn--primary"
            onclick={() => trigger(source)}
            disabled={pending.has(source.id)}
          >
            {pending.has(source.id)
              ? $tStore('views.collection.triggering')
              : $tStore('views.collection.trigger')}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .collection {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .collection__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  .collection__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-md;
  }

  .collection__meta {
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
  }

  .collection__name {
    font-weight: 600;
  }

  .collection__last {
    font-size: 0.8125rem;
  }
</style>
